import React, { useState, useEffect } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import SelectDropdown from '../components/SelectDropdown';
import { adminService, SystemPromptInfo } from '../services/adminService';
import ConfirmDialog from '../components/ConfirmDialog';

const SystemPrompts: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState<SystemPromptInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);

  // 编辑/创建对话框状态
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<SystemPromptInfo | null>(null);
  const [editPromptId, setEditPromptId] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // 删除确认对话框状态
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingPrompt, setDeletingPrompt] = useState<SystemPromptInfo | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 加载提示词列表
  const loadPrompts = async () => {
    try {
      setLoading(true);
      const result = await adminService.getSystemPrompts(page, pageSize, search || undefined, isActiveFilter);
      setPrompts(result.prompts);
      setTotal(result.total);
    } catch (error: any) {
      console.error('加载提示词列表失败:', error);
      showToast(error.message || '加载提示词列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, isActiveFilter]);

  // 处理搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadPrompts();
      } else {
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 打开创建对话框
  const handleCreate = () => {
    setEditingPrompt(null);
    setEditPromptId('');
    setEditTitle('');
    setEditContent('');
    setEditDescription('');
    setEditIsActive(true);
    setShowEditDialog(true);
  };

  // 打开编辑对话框
  const handleEdit = (prompt: SystemPromptInfo) => {
    setEditingPrompt(prompt);
    setEditPromptId(prompt.promptId || '');
    setEditTitle(prompt.title);
    setEditContent(prompt.content);
    setEditDescription(prompt.description || '');
    setEditIsActive(prompt.isActive);
    setShowEditDialog(true);
  };

  // 保存提示词
  const handleSave = async () => {
    if (!editPromptId.trim() || !editTitle.trim() || !editContent.trim()) {
      showToast('提示词ID、标题和内容不能为空', 'error');
      return;
    }

    try {
      setSaving(true);
      if (editingPrompt) {
        // 更新
        await adminService.updateSystemPrompt(editingPrompt.id, {
          promptId: editPromptId.trim(),
          title: editTitle.trim(),
          content: editContent.trim(),
          description: editDescription.trim() || undefined,
          isActive: editIsActive,
        });
        showToast('提示词已更新', 'success');
      } else {
        // 创建
        await adminService.createSystemPrompt({
          promptId: editPromptId.trim(),
          title: editTitle.trim(),
          content: editContent.trim(),
          description: editDescription.trim() || undefined,
          isActive: editIsActive,
        });
        showToast('提示词已创建', 'success');
      }
      setShowEditDialog(false);
      loadPrompts();
    } catch (error: any) {
      console.error('保存提示词失败:', error);
      showToast(error.message || '保存提示词失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 打开删除确认对话框
  const handleDelete = (prompt: SystemPromptInfo) => {
    setDeletingPrompt(prompt);
    setShowDeleteDialog(true);
  };

  // 确认删除
  const handleConfirmDelete = async () => {
    if (!deletingPrompt) return;

    try {
      setDeleting(true);
      if (!deletingPrompt.promptId) {
        throw new Error('提示词ID不能为空');
      }
      await adminService.deleteSystemPrompt(deletingPrompt.promptId);
      showToast('提示词已删除', 'success');
      setShowDeleteDialog(false);
      setDeletingPrompt(null);
      loadPrompts();
    } catch (error: any) {
      console.error('删除提示词失败:', error);
      showToast(error.message || '删除提示词失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader
          title="提示词管理"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={handleCreate}
              className="px-4 py-1.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
            >
              新建
            </button>
          }
        />

        {/* 筛选栏 */}
        <div
          className="fixed left-0 right-0 z-40 bg-black/10 backdrop-blur-sm border-b border-white/10 px-4 py-2"
          style={{ top: '56px' }}
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索标题或描述..."
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <SelectDropdown
              value={isActiveFilter === undefined ? 'all' : isActiveFilter ? 'active' : 'inactive'}
              onChange={(val) => {
                const value = val as string;
                setIsActiveFilter(value === 'all' ? undefined : value === 'active');
              }}
              options={[
                { value: 'all', label: '全部' },
                { value: 'active', label: '启用' },
                { value: 'inactive', label: '禁用' },
              ]}
              className="mb-0"
            />
          </div>
        </div>

        {/* 内容区域 */}
        <div className="px-4 pt-28 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : prompts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">暂无提示词</div>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors"
              >
                创建第一个提示词
              </button>
            </div>
          ) : (
            <>
              <div className="mt-4 mb-4 text-sm text-white/60">共 {total} 个提示词</div>
              <div className="space-y-3">
                {prompts.map((prompt) => (
                  <div key={prompt.id} className="bg-white/5 border border-white/20 rounded-xl p-4 text-white/90">
                    {/* 第一行：提示词ID和操作按钮 */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-300 border border-purple-500/50 font-mono">
                          {prompt.promptId}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            prompt.isActive
                              ? 'bg-green-500/20 text-green-300 border border-green-500/50'
                              : 'bg-gray-500/20 text-gray-300 border border-gray-500/50'
                          }`}
                        >
                          {prompt.isActive ? '启用' : '禁用'}
                        </span>
                        {prompt.usageCount !== undefined && prompt.usageCount > 0 && (
                          <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-300 border border-blue-500/50">
                            使用中 ({prompt.usageCount})
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(prompt)}
                          className="px-3 py-1.5 bg-blue-500/20 text-blue-300 border border-blue-500/50 rounded-lg hover:bg-blue-500/30 transition-colors text-xs"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleDelete(prompt)}
                          disabled={prompt.usageCount !== undefined && prompt.usageCount > 0}
                          className={`px-3 py-1.5 border rounded-lg transition-colors text-xs ${
                            prompt.usageCount !== undefined && prompt.usageCount > 0
                              ? 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed'
                              : 'bg-red-500/20 text-red-300 border-red-500/50 hover:bg-red-500/30'
                          }`}
                          title={
                            prompt.usageCount !== undefined && prompt.usageCount > 0
                              ? '有监控任务正在使用，无法删除'
                              : '删除'
                          }
                        >
                          删除
                        </button>
                      </div>
                    </div>

                    {/* 标题行 */}
                    <div className="mb-3">
                      <h3 className="font-bold text-white text-lg mb-1">{prompt.title}</h3>
                      {prompt.description && <div className="text-white/60 text-sm">{prompt.description}</div>}
                    </div>

                    {/* 内容块 */}
                    <div className="mb-3 p-3 bg-white/5 rounded border border-white/10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-white/60 text-xs">提示词内容（预览）:</div>
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(prompt.content);
                              showToast('提示词内容已复制到剪贴板', 'success');
                            } catch (error: any) {
                              console.error('复制失败:', error);
                              showToast('复制失败', 'error');
                            }
                          }}
                          className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
                          title="复制提示词内容"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="text-white/70 text-xs max-h-32 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">
                        {prompt.content.substring(0, 500)}
                        {prompt.content.length > 500 && '...'}
                      </div>
                    </div>

                    {/* 更新时间 */}
                    <div className="text-white/40 text-xs">
                      更新时间: {new Date(prompt.updatedAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                ))}
              </div>

              {/* 分页 */}
              {total > pageSize && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20"
                  >
                    上一页
                  </button>
                  <span className="text-white/60 text-sm">
                    {page} / {Math.ceil(total / pageSize)}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(Math.ceil(total / pageSize), p + 1))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20"
                  >
                    下一页
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* 编辑/创建对话框 */}
        {showEditDialog && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEditDialog(false);
              }
            }}
          >
            <div
              className="bg-gray-900/95 backdrop-blur-md rounded-2xl border border-white/20 p-6 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{editingPrompt ? '编辑提示词' : '创建提示词'}</h3>
                <button
                  onClick={() => setShowEditDialog(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">提示词ID *</label>
                  <input
                    type="text"
                    value={editPromptId}
                    onChange={(e) => setEditPromptId(e.target.value)}
                    placeholder="请输入提示词ID（字符串格式，必填）"
                    required
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">标题 *</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="请输入提示词标题"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">描述（可选）</label>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="请输入提示词描述"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-white/70 text-sm">内容 *</label>
                    <button
                      onClick={async () => {
                        if (!editContent.trim()) {
                          showToast('没有内容可复制', 'error');
                          return;
                        }
                        try {
                          await navigator.clipboard.writeText(editContent);
                          showToast('提示词内容已复制到剪贴板', 'success');
                        } catch (error: any) {
                          console.error('复制失败:', error);
                          showToast('复制失败', 'error');
                        }
                      }}
                      className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
                      title="复制提示词内容"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    placeholder="请输入提示词内容"
                    rows={15}
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/30 font-mono text-sm"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                    className="w-4 h-4 rounded border-white/30 bg-white/10 text-green-500 focus:ring-green-500 focus:ring-offset-0 focus:ring-offset-transparent focus:ring-2"
                  />
                  <label htmlFor="isActive" className="text-white/70 text-sm cursor-pointer">
                    启用此提示词
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => setShowEditDialog(false)}
                  className="px-4 py-2 bg-white/10 border border-white/20 text-white/70 rounded-lg hover:bg-white/20 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !editPromptId.trim() || !editTitle.trim() || !editContent.trim()}
                  className="px-4 py-2 bg-green-500/20 border border-green-500/50 text-green-300 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 删除确认对话框 */}
        <ConfirmDialog
          show={showDeleteDialog && !!deletingPrompt}
          title="确认删除"
          message={`确定要删除提示词 "${deletingPrompt?.title}" 吗？此操作不可恢复。`}
          confirmText="确认删除"
          cancelText="取消"
          confirmButtonClass="bg-red-500/20 text-red-300 border-red-500/50 hover:bg-red-500/30"
          loading={deleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setDeletingPrompt(null);
          }}
        />
      </div>
    </GradientBackground>
  );
};

export default SystemPrompts;
